import React, { useEffect, useState } from 'react'
import api from '../api-client'
import Spinner from '../spinner'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import notify from '../notifications-helper'
import FormField from '../form-field'
import ImpactDataForm from '../impact-data-form'
import IntegrationForm from './integration-form'
import ValuesSortOrder from './values-sort-order'
import ValueWeightsForm from './value-weights'
import HighlightedDataPointsForm from './highlighted-data-points-form'
import { Customer, FinancialIntegration, validateOptionalBannoOAuthRedirectUrlBase, WillNotFund as WNFConfig } from '@ecountabl/lib'
import SelectedIcon from '../selected-icon'
import PartnershipsForm from './partnerships-form'
import FeaturedTagSortOrder from './featured-tag-sort-order'
import ImageUploader from '../image-uploader'
import RequestSection from './request-section'
import PromotionSection from './promotion-section'
import CustomProductEditor from './custom-product-editor'
import FeaturedBusinessesList from './featured-businesses-list'
import { v4 as uuidv4 } from 'uuid'

interface Props {
  match: {
    params: {
      id: string
    }
  }
}

type SectionErrors = {
  savingsImpact?: string | null,
  businessReferrals?: string | null,
  promotion?: string | null
}

type FormFinancialIntegration = FinancialIntegration & { isModified?: boolean }

const CustomerForm: React.FC<Props> = ({ match }: Props) => {
  const { id } = match.params
  const [customer, setCustomer] = useState<Partial<Customer>>({
    willNotFund: {},
    config: {
      valuesSortOrder: {}
    }
  })
  const [modifiedIntegrations, setModifiedIntegrations] = useState({})
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [impactAreas, setImpactAreas] = useState([])
  const [sectionErrors, setSectionErrors] = useState<SectionErrors>({})
  const dispatch = useDispatch()
  const history = useHistory()

  const isBlank = (s?: string) => !s || !s.trim()

  const getSavingsImpactError = (s?: any): string | null => {
    if (!s) return null
    const messageEmpty = isBlank(s.message)
    const r = s.request
    const requestEmpty = !r || (isBlank((r as any).name) && isBlank(r.url) && isBlank(r.headers) && isBlank(r.body))
    if (messageEmpty && requestEmpty) return null
    const urlValid = !!(r && !isBlank(r.url))
    if (messageEmpty || !urlValid) {
      return 'Savings Impact section incomplete: message and request URL are required.'
    }
    return null
  }

  const getBusinessReferralsError = (s?: any): string | null => {
    if (!s) return null
    const messageEmpty = isBlank(s.message)
    const r = s.request
    const requestEmpty = !r || (isBlank((r as any).name) && isBlank(r.url) && isBlank(r.headers) && isBlank(r.body))
    if (messageEmpty && requestEmpty) return null
    const urlValid = !!(r && !isBlank(r.url))
    if (messageEmpty || !urlValid) {
      return 'Business Referral section incomplete: message and request URL are required.'
    }
    return null
  }

  const getPromotionError = (p?: any): string | null => {
    if (!p) return null
    const titleEmpty = isBlank(p.sectionTitle)
    const textEmpty = isBlank(p.promotionText)
    const items: any[] = Array.isArray(p.items) ? p.items : []
    const itemHasContent = (it: any) => it && (!isBlank(it.name) || !isBlank(it.url) || !isBlank(it.description) || !isBlank(it.image))
    const anyItemHasContent = items.some(itemHasContent)
    const allEmpty = titleEmpty && textEmpty && !anyItemHasContent
    if (allEmpty) return null
    const hasValidItem = items.some(i => i && !isBlank(i.name) && !isBlank(i.url))
    const isComplete = !titleEmpty && !textEmpty && hasValidItem
    if (!isComplete) {
      return 'Promotion section incomplete: section title, text, and at least one item with name and URL are required.'
    }
    return null
  }

  useEffect(() => {
    const init = async () => {
      if (id === 'new') {
        setLoading(false)
        return
      }

      try {
        const [_customer, _impactAreas] = await Promise.all([
          api.getCustomer(id),
          api.getImpactAreas()
        ])

        const integrations = Array.isArray((_customer as any).integrations)
          ? (_customer as any).integrations.map((i) => ({
            ...i,
            integrationId: i.integrationId || uuidv4(),
          }))
          : []

        setCustomer({
          willNotFund: {},
          ..._customer,
          integrations,
        })
        setImpactAreas(_impactAreas.list)
        setLoading(false)
      } catch (ex) {
        setLoading(false)
      }
    }

    init()
  }, [api.getValue, id])

  const handleSubmit = async (event) => {
    setSubmitting(true)
    event.preventDefault()
    try {
      const payload = { ...customer }
      const errors = Object.values(sectionErrors).filter(Boolean)
      if (errors.length > 0) {
        errors.forEach(err => dispatch(notify.error({ message: err })))
        setSubmitting(false)
        return
      }

      // Remove legacy field if present
      delete (payload as any).customRequests

      // Validate integrations: require non-empty integrationId and ensure uniqueness
      const rawIntegrations: any[] = Array.isArray((payload as any).integrations) ? (payload as any).integrations : []
      const normalizedIds = rawIntegrations.map((i: any) => (typeof i?.integrationId === 'string' ? i.integrationId.trim() : ''))
      const blankIdxs = normalizedIds
        .map((id, idx) => ({ id, idx }))
        .filter(x => !x.id)
        .map(x => x.idx)
      if (blankIdxs.length > 0) {
        dispatch(notify.error({ message: `Each integration must have an Integration ID (missing on integration #${blankIdxs[0] + 1}).` }))
        setSubmitting(false)
        return
      }
      const counts = new Map<string, number>()
      for (const id of normalizedIds) counts.set(id, (counts.get(id) || 0) + 1)
      const dupes = Array.from(counts.entries()).filter(([, c]) => c > 1).map(([id]) => id)
      if (dupes.length > 0) {
        dispatch(notify.error({ message: `Integration IDs must be unique. Duplicate(s): ${dupes.join(', ')}` }))
        setSubmitting(false)
        return
      }

      for (let integrationIndex = 0; integrationIndex < rawIntegrations.length; integrationIndex++) {
        const row = rawIntegrations[integrationIndex]
        if (row?.platform !== 'banno') continue
        const redirectCheck = validateOptionalBannoOAuthRedirectUrlBase(row?.bannoRedirectUrlBase)
        if (!redirectCheck.ok) {
          dispatch(
            notify.error({
              message: `Integration #${integrationIndex + 1} (Banno): ${redirectCheck.message}`,
            })
          )
          setSubmitting(false)
          return
        }
      }

      // Persist normalized IDs back into payload so server receives trimmed values.
      if ((payload as any).integrations) {
        payload.integrations = rawIntegrations.map((i: any, idx: number) => {
          const next = {
            ...i,
            integrationId: normalizedIds[idx],
          }
          if (next.platform === 'banno') {
            const redirectCheck = validateOptionalBannoOAuthRedirectUrlBase(next.bannoRedirectUrlBase)
            if (redirectCheck.ok && redirectCheck.origin) {
              next.bannoRedirectUrlBase = redirectCheck.origin
            } else {
              delete next.bannoRedirectUrlBase
            }
          }
          return next
        })
      }

      payload.integrations?.forEach(i => delete (i as any).isModified)
      
      setSubmitting(false)
      if (id === 'new') {
        const res = await api.postCustomer(payload as Customer)
        history.push(`/customers/${res._id}`)
      } else {
        await api.putCustomer(payload as Customer)
      }
      dispatch(notify.success({ message: "Customer saved successfully" }))
      setCustomer(payload)
    } catch (err) {
      setSubmitting(false)
      dispatch(notify.error({ message: `Error saving value: ${err.message}` }))
    }
  }

  const handleChange = (event) => {
    setCustomer({ ...customer, [event.target.name]: event.target.value })
  }

  return (
    <>
      {loading ? (
        <div className="d-flex vh-100 justify-content-center align-items-center ">
          <Spinner />
        </div>
      ) : (
        <div className="container mt-5">
          <h2>Edit Customer</h2>
          <div>
            <div>
              <FormField
                value={customer.name || ''}
                onChange={handleChange}
                label="Name"
                type="text"
                field="name"
              />
              <div className="form-group">
                <label>Plaid Institution IDs</label>
                {/** Row with wrap mapping ids to input fields with a delete button. the view containing the input and delete button will have a background color of red */}
                <div style={{
                  display: 'flex',
                  flexDirection: 'row',
                  flexWrap: 'wrap',
                  gap: '0.5em'
                }}>
                  {customer.plaidInstitutionIDs?.map((id, i) => (
                    <div key={i} style={{display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '1em', padding: '0.5em', backgroundColor: "#eee", borderRadius: 10}}>
                      <input
                        type="text"
                        value={id}
                        onChange={ev => {
                          const ids = [...customer.plaidInstitutionIDs]
                          ids[i] = ev.target.value
                          setCustomer({ ...customer, plaidInstitutionIDs: ids })
                        }}
                      />
                      <i className="fa fa-trash" style={{cursor: 'pointer'}} onClick={() => {
                        const ids = [...customer.plaidInstitutionIDs]
                        ids.splice(i, 1)
                        setCustomer({ ...customer, plaidInstitutionIDs: ids })
                      }} />
                    </div>
                  ))}
                </div>
                <button type="button" className="btn btn-normal" onClick={() => setCustomer({ ...customer, plaidInstitutionIDs: [...(customer.plaidInstitutionIDs || []), ''] })}>Add Institution ID</button>
              </div>
              <div className="form-group">
                <label>Theme</label>
                <select className="form-control" value={customer.config?.theme} onChange={ev => {
                  setCustomer({
                    ...customer,
                    config: {
                      ...customer.config,
                      theme: ev.target.value
                    }
                  })
                }}>
                  <option value="dark">Dark</option>
                  <option value="light">Light</option>
                  <option value="climateFirst">Climate First</option>
                </select>
              </div>
              <div className="form-group">
                <label>Logo</label>
                <ImageUploader
                  imgSrc={customer.logo}
                  onChange={logo => setCustomer({ ...customer, logo })}
                  
                />
              </div>
              <div style={{ marginBottom: '1.5em' }}>
                <h4>Integrations</h4>
                <div className="integrations">
                  {customer.integrations?.map((integration, i) => <IntegrationForm
                    key={i}
                    integration={integration}
                    onChange={ev => {
                      const integrations = [...customer.integrations]
                      const current: FormFinancialIntegration = integrations[i] || { platform: 'finastra', integrationId: uuidv4() }
                      current[ev.target.name] = (ev.target as any).value
                      current.isModified = true
                      integrations[i] = current
                      setCustomer({
                        ...customer,
                        integrations
                      })
                    }}
                    onDelete={() => {
                      const integrations = [...customer.integrations]
                      integrations.splice(i, 1)
                      setCustomer({
                        ...customer,
                        integrations
                      })
                    }}
                  />)}
                  {customer.integrations?.length === 0 ? <div className="alert alert-secondary">No integrations configured</div> : null}
                </div>
                <button
                  type="button"
                  className="btn btn-normal"
                  onClick={() => setCustomer({
                    ...customer,
                    integrations: [...customer.integrations || [], { platform: 'finastra', integrationId: uuidv4() } as any]
                  })}
                >Add Integration</button>
              </div>
            </div>
          </div>
          <div>
            {/* <div>
              <h4>Will Not Fund</h4>
              {Object.keys(WNFConfig).map(key => (
                <div className="form-group" key={key}>
                  <label htmlFor={`wnf_${key}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                    {WNFConfig[key].label}
                    <SelectedIcon icon={WNFConfig[key].icon} size={20} style={{ paddingLeft: '0.3em' }} />
                  </label>
                  <input
                    id={`wnf_${WNFConfig[key]}`}
                    type="checkbox"
                    className="form-control"
                    checked={customer.willNotFund[key] || false}
                    onChange={ev => setCustomer({
                      ...customer,
                      willNotFund: {
                        ...customer.willNotFund,
                        [key]: !customer.willNotFund[key]
                      }
                    })}
                  />
                </div>
              ))}
            </div> */}
            <div className="form-group">
              <ValuesSortOrder
                sortOrder={customer.config.valuesSortOrder || {}}
                onChange={order => setCustomer({
                  ...customer,
                  config: {
                    ...customer.config,
                    valuesSortOrder: order
                  }
                })}
              />
            </div>
            <div className="form-group">
              <ValueWeightsForm
                weights={customer.config?.valueWeights || {}}
                onChange={valueWeights => setCustomer({
                  ...customer,
                  config: {
                    ...customer.config,
                    valueWeights
                  }
                })}
              />
            </div>
            <div className="form-group">
              <FeaturedTagSortOrder
                topTags={customer.config?.topTags || []}
                onChange={topTags => setCustomer({
                  ...customer,
                  config: {
                    ...customer.config,
                    topTags
                  }
                })}
              />
            </div>
            <div className="form-group">
              <FeaturedBusinessesList
                value={customer.featuredBusinesses}
                onChange={(featuredBusinesses) => setCustomer({
                  ...customer,
                  featuredBusinesses
                })}
              />
            </div>
          </div>
          {
            /*
            <div style={{ marginBottom: '1.5em' }}>
            <HighlightedDataPointsForm
              datapoints={customer.highlightedDataPoints || []}
              impactAreas={impactAreas}
              onChange={datapoints => setCustomer({
                ...customer,
                highlightedDataPoints: datapoints
              })}
            />
          </div>
            */
          }
          <div style={{ marginBottom: '1.5em' }}>
            <PromotionSection
              label="Promotion"
              value={customer.promotion}
              isVisible={(() => {
                const p = customer.promotion as any
                if (!p) return false
                const titleValid = typeof p.sectionTitle === 'string' && p.sectionTitle.trim()
                const textValid = typeof p.promotionText === 'string' && p.promotionText.trim()
                const items = Array.isArray(p.items) ? p.items : []
                const hasValidItem = items.some(i => i && typeof i.name === 'string' && i.name.trim() && typeof i.url === 'string' && i.url.trim())
                return !!(titleValid && textValid && hasValidItem)
              })()}
            onChange={(promotion) => {
              const error = getPromotionError(promotion)
              setSectionErrors(prev => ({ ...prev, promotion: error }))
              setCustomer({ ...customer, promotion })
            }}
            />
          </div>
          <div style={{ marginBottom: '1.5em' }}>
            <RequestSection
              label="Savings Impact"
              value={customer.savingsImpact}
              isVisible={(() => {
                const s = customer.savingsImpact
                if (!s) return false
                const messageVisible = !!(s.message && s.message.trim())
                const r = s.request
                const requestVisible = !!(r && ((r.url && r.url.trim()) || (r.headers && r.headers.trim()) || (r.body && r.body.trim())))
                return messageVisible || requestVisible
              })()}
            onChange={(section) => {
              const error = getSavingsImpactError(section)
              setSectionErrors(prev => ({ ...prev, savingsImpact: error }))
              setCustomer({
                ...customer,
                savingsImpact: section
              })
            }}
            />
          </div>

          <div style={{ marginBottom: '1.5em' }}>
            <RequestSection
              label="Business Referrals"
              value={customer.businessReferrals}
              isVisible={(() => {
                const s = customer.businessReferrals
                if (!s) return false
                const messageVisible = !!(s.message && s.message.trim())
                const r = s.request
                const requestVisible = !!(r && ((r.url && r.url.trim()) || (r.headers && r.headers.trim()) || (r.body && r.body.trim())))
                return messageVisible || requestVisible
              })()}
            onChange={(section) => {
              const error = getBusinessReferralsError(section)
              setSectionErrors(prev => ({ ...prev, businessReferrals: error }))
              setCustomer({
                ...customer,
                businessReferrals: section
              })
            }}
            />
          </div>

          <div style={{ marginBottom: '1.5em' }}>
            <CustomProductEditor
              customer={customer}
              onChange={(customProduct) => {
                setCustomer({
                  ...customer,
                  customProduct: customProduct
                })
              }}
            />
          </div>

          <div style={{ marginBottom: '1.5em' }}>
            <PartnershipsForm
              partnerships={customer.partnerships || []}
              partnershipHeader={customer.partnershipHeader}
              partnershipSubtext={customer.partnershipSubtext}
              onChange={details => setCustomer({
                ...customer,
                ...details
              })}
            />
          </div>
          <div className="mt-5">
            <form onSubmit={handleSubmit}>
              <div className="pt-4 border-top mb-5">
                <button type="submit" className="btn btn-primary">
                  {submitting ? (
                    <Spinner />
                  ) : (
                    <div>Save Customer</div>
                  )}
                </button>
              </div>
            </form>
          </div>
          {/* customer._id ? <ImpactDataForm customer={customer} /> : (
            <div className="alert alert-info">
              Customer must be saved before adding impact data reports
            </div>
          ) */}
        </div>
      )}
    </>
  )
}

export default CustomerForm
